import React, { useState} from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import ThemeToggle from './ThemeToggle';
import { useTheme } from '../hooks/useTheme';

const Navigation: React.FC<{ scrollY: number }> = ({ scrollY }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { name: 'Home', href: '#home', isHash: true },
    { name: 'About', href: '#about', isHash: true },
    { name: 'Services', href: '#services', isHash: true },
    { name: 'Achievements', href: '#achievements', isHash: true },
    { name: 'Experience', href: '#experience', isHash: true },
    { name: 'Blog', href: '/blog', isHash: false },
    // { name: 'Testimonials', href: '#testimonials' },
    { name: 'Contact', href: '#contact', isHash: true },
  ];

  const handleNavClick = (href: string, isHash: boolean) => {
    if (isHash) {
      // Handle hash links - scroll to section if on homepage, otherwise navigate to homepage with hash
      if (location.pathname === '/') {
        const element = document.querySelector(href);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        // Navigate to homepage with hash using React Router
        navigate(`/${href}`);
      }
    }
    setIsMenuOpen(false);
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrollY > 50 
        ? `glass-nav-scrolled ${isDark ? 'glass-dark-nav-scrolled' : 'glass-light-nav-scrolled'} shadow-lg` 
        : 'bg-transparent'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center gap-2">
            {/* <span className={`text-2xl font-bold gradient-text`}>
              KM
            </span> */}
            <img 
              src="/OrangeMmariSignature.png" 
              alt="Kennedy Mmari Signature" 
              className="h-8 w-auto"
              onError={(e) => {
                // Hide signature if image doesn't exist
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
              }}
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:block">
            <div className="ml-10 flex items-baseline space-x-8">
              {navItems.map((item) => (
                item.isHash ? (
                  <button
                    key={item.name}
                    onClick={() => handleNavClick(item.href, item.isHash)}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-300 hover:text-sunrise-orange cursor-pointer ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-3 py-2 text-sm font-medium transition-colors duration-300 hover:text-sunrise-orange cursor-pointer ${
                      isDark ? 'text-gray-300' : 'text-gray-700'
                    } ${location.pathname === item.href ? 'text-sunrise-orange' : ''}`}
                  >
                    {item.name}
                  </Link>
                )
              ))}
            </div>
          </div>

          {/* Theme Toggle */}
          <div className="hidden md:block">
            <ThemeToggle />
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center space-x-2">
            <ThemeToggle />
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`p-2 rounded-md cursor-pointer ${isDark ? 'text-white' : 'text-gray-700'}`}
            >
              {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMenuOpen && (
        <div className={`md:hidden ${isDark ? 'bg-gray-900/95' : 'bg-white/95'} glass-nav-scrolled backdrop-blur-xl`}>
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navItems.map((item) => (
              item.isHash ? (
                <button
                  key={item.name}
                  onClick={() => handleNavClick(item.href, item.isHash)}
                  className={`block px-3 py-2 text-base font-medium w-full text-left transition-colors duration-300 hover:text-sunrise-orange cursor-pointer ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  }`}
                >
                  {item.name}
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`block px-3 py-2 text-base font-medium w-full text-left transition-colors duration-300 hover:text-sunrise-orange cursor-pointer ${
                    isDark ? 'text-gray-300' : 'text-gray-700'
                  } ${location.pathname === item.href ? 'text-sunrise-orange' : ''}`}
                >
                  {item.name}
                </Link>
              )
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navigation;
